<template>
  <v-container fluid class="dashboard-container pa-6">
    <!-- Header do Dashboard -->
    <div class="dashboard-header mb-6">
      <h2 class="dashboard-title">Dashboard de Gestão Escolar</h2>
      <p class="dashboard-subtitle">
        Central de controle e monitoramento das operações escolares
      </p>
    </div>

    <!-- Cards de Métricas Principais -->
    <v-row class="mb-6">
      <v-col cols="12" md="4">
        <v-card class="metric-card" elevation="3">
          <v-card-text>
            <div class="d-flex justify-space-between align-center">
              <div>
                <div class="metric-label">Total de Alunos</div>
                <div class="metric-value">{{ totalStudents }}</div>
                <div class="metric-trend positive">
                  <v-icon small color="success">mdi-trending-up</v-icon>
                  <span>+{{ studentsTrendMonth }}%</span> este mês
                </div>
              </div>
              <div class="metric-icon-wrapper primary">
                <v-icon size="40" color="white">mdi-account-group</v-icon>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card class="metric-card" elevation="3">
          <v-card-text>
            <div class="d-flex justify-space-between align-center">
              <div>
                <div class="metric-label">Matrículas Ativas</div>
                <div class="metric-value">{{ totalRegistrations }}</div>
                <div class="metric-trend positive">
                  <v-icon small color="success">mdi-trending-up</v-icon>
                  <span>+{{ registrationsTrendMonth }}%</span> este mês
                </div>
              </div>
              <div class="metric-icon-wrapper success">
                <v-icon size="40" color="white">mdi-clipboard-check</v-icon>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card class="metric-card" elevation="3">
          <v-card-text>
            <div class="d-flex justify-space-between align-center">
              <div>
                <div class="metric-label">Total de Turmas</div>
                <div class="metric-value">{{ totalTeams }}</div>
                <div class="metric-trend neutral">
                  <v-icon small>mdi-minus</v-icon>
                  <span>Estável</span>
                </div>
              </div>
              <div class="metric-icon-wrapper warning">
                <v-icon size="40" color="white">mdi-google-classroom</v-icon>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Gráficos Principais -->
    <v-row class="mb-6">
      <v-col cols="12" md="6">
        <v-card class="chart-card" elevation="3">
          <v-card-title class="chart-title">
            <v-icon class="mr-2" color="primary">mdi-chart-bar</v-icon>
            Alunos Cadastrados por Mês
          </v-card-title>
          <v-card-text>
            <div class="app--chart" ref="chartStudents">
              <Bar :data="chartDataStudents" :options="chartOptions" />
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="chart-card" elevation="3">
          <v-card-title class="chart-title">
            <v-icon class="mr-2" color="success">mdi-chart-line</v-icon>
            Matrículas Efetivadas por Mês
          </v-card-title>
          <v-card-text>
            <div class="app--chart" ref="chartRegistrations">
              <Bar :data="chartDataRegistrations" :options="chartOptions" />
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Gráfico de Alunos por Curso -->
    <v-row class="mb-6">
      <v-col cols="12">
        <v-card class="chart-card" elevation="3">
          <v-card-title class="chart-title">
            <v-icon class="mr-2" color="purple">mdi-school</v-icon>
            Alunos Matriculados por Curso
          </v-card-title>
          <v-card-text>
            <div class="app--chart-large">
              <Bar :data="chartDataStudentsPerCourse" :options="chartOptions" />
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Gráfico de Alunos por Turma -->
    <v-row class="mb-6">
      <v-col cols="12">
        <v-card class="chart-card" elevation="3">
          <v-card-title class="chart-title">
            <v-icon class="mr-2" color="warning">mdi-chart-box</v-icon>
            Alunos Matriculados por Turma
          </v-card-title>
          <v-card-text>
            <div class="chart-teams-scroll">
              <div :style="{ height: chartDataStudentsPerTeams.labels.length * 28 + 'px', minHeight: '300px' }">
                <Bar :data="chartDataStudentsPerTeams" :options="chartOptionsHorizontal" />
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Indicadores Financeiros -->
    <v-row class="mb-6">
      <!-- Turmas com mais inadimplentes -->
      <v-col cols="12" md="4">
        <v-card class="chart-card" elevation="3">
          <v-card-title class="chart-title">
            <v-icon class="mr-2" color="error">mdi-account-alert</v-icon>
            Turmas com Mais Inadimplentes
          </v-card-title>
          <v-card-text class="pa-0">
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Turma</th>
                  <th class="text-center">Inadimplentes</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in teamsWithMostDefaulters" :key="i">
                  <td class="text-truncate" style="max-width:220px">
                    <router-link :to="{ name: 'team-edit', params: { id: item.id } }">{{ item.name }}</router-link>
                  </td>
                  <td class="text-center">
                    <v-chip small color="error" text-color="white">{{ item.total_defaulters }}</v-chip>
                  </td>
                </tr>
                <tr v-if="!teamsWithMostDefaulters.length">
                  <td colspan="2" class="text-center grey--text">Sem dados</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Top 10 alunos mais inadimplentes -->
      <v-col cols="12" md="4">
        <v-card class="chart-card" elevation="3">
          <v-card-title class="chart-title">
            <v-icon class="mr-2" color="deep-orange">mdi-account-cash</v-icon>
            Top 10 Alunos Inadimplentes
          </v-card-title>
          <v-card-text class="pa-0">
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Aluno</th>
                  <th class="text-center">Parcelas</th>
                  <th class="text-right">Total (R$)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in topDefaultingStudents" :key="i">
                  <td class="text-truncate" style="max-width:140px">
                    <router-link :to="{ name: 'student-edit', params: { id: item.id } }">{{ item.name }}</router-link>
                  </td>
                  <td class="text-center">{{ item.total_pending }}</td>
                  <td class="text-right">{{ Number(item.total_value).toFixed(2) }}</td>
                </tr>
                <tr v-if="!topDefaultingStudents.length">
                  <td colspan="3" class="text-center grey--text">Sem dados</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Últimas 10 pendências vencidas -->
      <v-col cols="12" md="4">
        <v-card class="chart-card" elevation="3">
          <v-card-title class="chart-title">
            <v-icon class="mr-2" color="warning">mdi-clock-alert</v-icon>
            Últimas 10 Pendências Vencidas
          </v-card-title>
          <v-card-text class="pa-0">
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Aluno</th>
                  <th class="text-center">Vencto</th>
                  <th class="text-center">Dias</th>
                  <th class="text-right">R$</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in lastOverduePendencies" :key="i">
                  <td class="text-truncate" style="max-width:120px">
                    <router-link :to="{ name: 'student-edit', params: { id: item.student_id } }">{{ item.student }}</router-link>
                  </td>
                  <td class="text-center">{{ item.due_date }}</td>
                  <td class="text-center">
                    <v-chip x-small color="error" text-color="white">{{ item.days_overdue }}d</v-chip>
                  </td>
                  <td class="text-right">{{ Number(item.total != null ? item.total : item.value).toFixed(2) }}</td>
                </tr>
                <tr v-if="!lastOverduePendencies.length">
                  <td colspan="4" class="text-center grey--text">Sem dados</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Seção de Relatórios -->
    <div class="reports-section">
      <div class="section-header mb-4">
        <h3 class="section-title">
          <v-icon class="mr-2" color="teal">mdi-file-chart</v-icon>
          Relatórios Disponíveis
        </h3>
      </div>

      <v-row>
        <v-col v-for="(link, index) in links" :key="index" cols="12" sm="6" md="4" lg="3">
          <router-link :to="link.path" class="no-decoration">
            <v-card class="report-card" elevation="2" hover>
              <v-card-text class="d-flex flex-column align-center justify-center pa-4">
                <div class="report-icon-wrapper" :class="getCardColorClass(index)">
                  <v-icon size="32" color="white">{{ getIcon(link.action) }}</v-icon>
                </div>
                <span class="report-title mt-3">{{ link.action }}</span>
              </v-card-text>
            </v-card>
          </router-link>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import api from "@/services";
import { Bar } from "vue-chartjs";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, ChartDataLabels);

export default {
  components: { Bar },
  data() {
    return {
      totalStudents: 0,
      totalRegistrations: 0,
      totalTeams: 0,
      studentsTrendMonth: 0,
      registrationsTrendMonth: 0,
      chartDataStudentsPerTeams: { labels: [], datasets: [] },
      chartDataStudentsPerCourse: { labels: [], datasets: [] },
      chartDataStudents: { labels: [], datasets: [] },
      chartDataRegistrations: { labels: [], datasets: [] },
      teamsWithMostDefaulters: [],
      topDefaultingStudents: [],
      lastOverduePendencies: [],
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          datalabels: {
            anchor: "end",
            align: "top",
            color: "#555",
            font: { size: 11, weight: "600" },
            formatter: (value) => value > 0 ? value : "",
          },
        },
        scales: {
          y: { beginAtZero: true, grid: { color: "#f0f0f0" } },
          x: { grid: { display: false } },
        },
      },
      chartOptionsHorizontal: {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          datalabels: {
            anchor: "end",
            align: "right",
            color: "#555",
            font: { size: 11, weight: "600" },
            formatter: (value) => value > 0 ? value : "",
          },
        },
        scales: {
          x: { beginAtZero: true, grid: { color: "#f0f0f0" } },
          y: { grid: { display: false }, ticks: { font: { size: 11 } } },
        },
      },
      links: [
        { action: "Geral", path: { name: "general-report-of-students" } },
        { action: "Diário de classe", path: { name: "diario-de-classe" } },
        { action: "Relatório financeiro por turma", path: { name: "relatorio-financeiro-por-turma" } },
        { action: "Alunos por turma", path: { name: "alunos-por-turma" } },
        { action: "Dados completos dos alunos por turma", path: { name: "alunos-dados-completo-por-turma" } },
        { action: "Atestado de conclusão de curso", path: { name: "atestado-de-conclusao-de-curso" } },
        { action: "Declaração de matrícula", path: { name: "declaracao-de-matricula" } },
        { action: "Boletim do aluno", path: { name: "boletim-do-aluno" } },
        { action: "Relatório de repasse", path: { name: "relatorio-de-repasse" } },
        { action: "Relatório de desempenho anual", path: { name: "relatorio-de-desempenho-anual" } },
        { action: "Relatório de inadimplentes", path: { name: "inadimplentes" } },
        { action: "Gerar contrato", path: { name: "gerar-contrato" } },
        { action: "Relatório financeiro com filtro", path: { name: "relatorio-financeiro-com-filtro" } },
        { action: "Relatório de livros", path: { name: "relatorio-de-livros" } },
        { action: "Relatório de alunos com documentos", path: { name: "relatorio-alunos-com-documentos" } },
        { action: "Relatório de agendas", path: { name: "relatorio-agenda-semanal" } },
        { action: "Relatório de alunos matriculados", path: { name: "relatorio-alunos-matriculados" } },
        { action: "Relatório acompanhamento de envio", path: { name: "acompanhamento-de-envio" } },
      ],
    };
  },
  methods: {
    async fetchStudentsPerTeams() {
      try {
        const response = await api.get("/indicators/number-students-per-teams");
        const data = response.data;
        this.totalTeams = data.length;
        this.chartDataStudentsPerTeams = {
          labels: data.map((item) => item.name.toUpperCase()),
          datasets: [{ label: "Alunos", backgroundColor: "#009688", borderRadius: 8, data: data.map((item) => item.total_students) }],
        };
      } catch (error) {
        console.error("Erro ao buscar dados de times:", error);
      }
    },
    async fetchStudentsPerCourse() {
      try {
        const response = await api.get("/indicators/number-students-per-course");
        const data = response.data;
        this.chartDataStudentsPerCourse = {
          labels: data.map((item) => item.name),
          datasets: [{ label: "Alunos", backgroundColor: "#9C27B0", borderRadius: 8, data: data.map((item) => item.total_students) }],
        };
      } catch (error) {
        console.error("Erro ao buscar dados de cursos:", error);
      }
    },
    async fetchRegistrationsData() {
      try {
        const response = await api.get("/indicators/number-registrations-per-month");
        const data = response.data;
        this.totalRegistrations = data.reduce((acc, item) => acc + item.total_registrations, 0);
        if (data.length >= 2) {
          const lastMonth = data[data.length - 1].total_registrations;
          const previousMonth = data[data.length - 2].total_registrations;
          this.registrationsTrendMonth = previousMonth > 0 ? Math.round(((lastMonth - previousMonth) / previousMonth) * 100) : 0;
        }
        this.chartDataRegistrations = {
          labels: data.map((item) => item.month),
          datasets: [{ label: "Matrículas", backgroundColor: "#4CAF50", borderRadius: 8, data: data.map((item) => item.total_registrations) }],
        };
      } catch (error) {
        console.error("Erro ao buscar dados de registros:", error);
      }
    },
    async fetchStudentsData() {
      try {
        const response = await api.get("/indicators/number-students-per-month");
        const data = response.data;
        this.totalStudents = data.reduce((acc, item) => acc + item.total_students, 0);
        if (data.length >= 2) {
          const lastMonth = data[data.length - 1].total_students;
          const previousMonth = data[data.length - 2].total_students;
          this.studentsTrendMonth = previousMonth > 0 ? Math.round(((lastMonth - previousMonth) / previousMonth) * 100) : 0;
        }
        this.chartDataStudents = {
          labels: data.map((item) => item.month),
          datasets: [{ label: "Alunos", backgroundColor: "#2196F3", borderRadius: 8, data: data.map((item) => item.total_students) }],
        };
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
      }
    },
    async fetchDefaultersData() {
      try {
        const [teams, students, pendencies] = await Promise.all([
          api.get("/indicators/teams-with-most-defaulters"),
          api.get("/indicators/top-defaulting-students"),
          api.get("/indicators/last-overdue-pendencies"),
        ]);
        this.teamsWithMostDefaulters = teams.data;
        this.topDefaultingStudents = students.data;
        this.lastOverduePendencies = pendencies.data;
      } catch (error) {
        console.error("Erro ao buscar dados de inadimplência:", error);
      }
    },
    getCardColorClass(index) {
      const classes = ["teal-bg", "blue-bg", "purple-bg", "orange-bg", "green-bg", "indigo-bg"];
      return classes[index % classes.length];
    },
    getIcon(action) {
      const icons = {
        Geral: "mdi-view-dashboard",
        "Diário de classe": "mdi-book-open-page-variant",
        "Relatório financeiro por turma": "mdi-currency-usd",
        "Alunos por turma": "mdi-account-multiple",
        "Dados completos dos alunos por turma": "mdi-account-details",
        "Atestado de conclusão de curso": "mdi-certificate",
        "Declaração de matrícula": "mdi-file-document",
        "Boletim do aluno": "mdi-file-chart",
        "Relatório de repasse": "mdi-clipboard-text",
        "Gerar contrato": "mdi-clipboard-text",
        "Relatório de desempenho anual": "mdi-chart-line",
        "Relatório de inadimplentes": "mdi-alert-circle",
        "Relatório financeiro com filtro": "mdi-filter",
        "Relatório de livros": "mdi-book-open-variant",
        "Relatório de alunos com documentos": "mdi-account-box-multiple",
        "Relatório de agendas": "mdi-calendar-multiselect",
        "Relatório de alunos matriculados": "mdi-clipboard-account",
        "Relatório acompanhamento de envio": "mdi-truck-delivery",
      };
      return icons[action] || "mdi-file";
    },
  },
  mounted() {
    this.fetchRegistrationsData();
    this.fetchStudentsData();
    this.fetchStudentsPerTeams();
    this.fetchStudentsPerCourse();
    this.fetchDefaultersData();
  },
};
</script>

<style scoped>
.dashboard-container { background: #f5f7fa; min-height: 100vh; }
.dashboard-header { margin-bottom: 2rem; }
.dashboard-title { font-size: 2rem; font-weight: 600; color: #2c3e50; margin-bottom: 0.5rem; }
.dashboard-subtitle { font-size: 1rem; color: #7f8c8d; margin-bottom: 0; }
.metric-card { border-radius: 12px; transition: transform 0.2s, box-shadow 0.2s; background: white; overflow: hidden; }
.metric-card:hover { transform: translateY(-4px); }
.metric-label { font-size: 0.875rem; color: #7f8c8d; font-weight: 500; text-transform: uppercase; letter-spacing: 0.5px; }
.metric-value { font-size: 2.25rem; font-weight: 700; color: #2c3e50; margin: 0.5rem 0; }
.metric-trend { font-size: 0.875rem; display: flex; align-items: center; gap: 4px; }
.metric-trend.positive { color: #4caf50; }
.metric-trend.negative { color: #f44336; }
.metric-trend.neutral { color: #ff9800; }
.metric-icon-wrapper { width: 70px; height: 70px; border-radius: 12px; display: flex; align-items: center; justify-content: center; }
.metric-icon-wrapper.primary { background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%); }
.metric-icon-wrapper.success { background: linear-gradient(135deg, #4caf50 0%, #388e3c 100%); }
.metric-icon-wrapper.warning { background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%); }
.chart-card { border-radius: 12px; background: white; transition: box-shadow 0.2s; }
.chart-title { font-size: 1.125rem; font-weight: 600; color: #2c3e50; padding: 1.25rem 1.5rem 0.75rem; }
.app--chart { height: 300px; width: 100%; padding: 1rem 0; }
.app--chart-large { height: 350px; width: 100%; padding: 1rem 0; }
.chart-teams-scroll { max-height: 600px; overflow-y: auto; padding: 0.5rem 0; }
.reports-section { margin-top: 2rem; }
.section-header { border-bottom: 2px solid #e0e0e0; padding-bottom: 0.75rem; }
.section-title { font-size: 1.5rem; font-weight: 600; color: #2c3e50; display: flex; align-items: center; }
.report-card { border-radius: 12px; transition: all 0.3s ease; background: white; height: 100%; cursor: pointer; }
.report-card:hover { transform: translateY(-6px); }
.report-icon-wrapper { width: 64px; height: 64px; border-radius: 12px; display: flex; align-items: center; justify-content: center; }
.report-icon-wrapper.teal-bg { background: linear-gradient(135deg, #009688 0%, #00796b 100%); }
.report-icon-wrapper.blue-bg { background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%); }
.report-icon-wrapper.purple-bg { background: linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%); }
.report-icon-wrapper.orange-bg { background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%); }
.report-icon-wrapper.green-bg { background: linear-gradient(135deg, #4caf50 0%, #388e3c 100%); }
.report-icon-wrapper.indigo-bg { background: linear-gradient(135deg, #3f51b5 0%, #303f9f 100%); }
.report-title { font-size: 0.9rem; font-weight: 500; color: #2c3e50; text-align: center; line-height: 1.4; display: block; }
.no-decoration { text-decoration: none; }
@media (max-width: 960px) {
  .dashboard-title { font-size: 1.5rem; }
  .metric-value { font-size: 1.75rem; }
  .metric-icon-wrapper { width: 60px; height: 60px; }
}
</style>
